import * as React from 'react';
import { Badge, Box, Chip, IconButton, Paper, Table, TableBody, TableCell, TableHead, TableRow, Tooltip, Typography } from '@mui/material';
import ErrorOutlineIcon from '@mui/icons-material/ErrorOutline';
import AssignmentTurnedInIcon from '@mui/icons-material/AssignmentTurnedIn';
import AssignmentLateIcon from '@mui/icons-material/AssignmentLate';
import { blue, green, grey, orange, red } from '@mui/material/colors';
import moment from 'moment';
import 'moment/locale/pt-br';
import api from '../services/api';

/**
 * Banner fixo de alarmes API Down (severity=6) - sempre visivel no topo,
 * independente dos filtros aplicados na tabela principal de alarmes.
 *
 * Atualiza via fetch inicial + eventos WebSocket (alarms-new / alarm-removed).
 *
 * Props:
 *   - source: opcional 'zabbix' | 'wazuh' (filtra so um tipo). Vazio = ambos.
 *   - onAck:  callback (alarm) => void disparado ao clicar em Reconhecer.
 *             Quem usa o banner abre o FormDialog de ack a partir desse callback.
 */
export default function ApiDownBanner({ source, onAck }) {
  const [items, setItems] = React.useState([]);

  const sortByClockDesc = React.useCallback((arr) => {
    return [...arr].sort((a, b) => new Date(b.clock) - new Date(a.clock));
  }, []);

  const matchesSource = React.useCallback((alarm) => {
    if (!source) return true;
    return (alarm.source || 'zabbix') === source;
  }, [source]);

  const fetchActive = React.useCallback(() => {
    const params = source ? { params: { source } } : undefined;
    api.get('dashboard/api-down-active/', params)
      .then(res => setItems(sortByClockDesc(res.data || [])))
      .catch(() => setItems([]));
  }, [source, sortByClockDesc]);

  // Fetch inicial + on-demand via custom event
  React.useEffect(() => {
    fetchActive();
    const handler = () => fetchActive();
    window.addEventListener('alarms-refresh', handler);
    return () => window.removeEventListener('alarms-refresh', handler);
  }, [fetchActive]);

  // Merge novos alarmes via WebSocket (so severity=6)
  React.useEffect(() => {
    const handleNew = (event) => {
      const incoming = event.detail;
      if (!incoming || !Array.isArray(incoming) || incoming.length === 0) return;
      const apiDownNew = incoming.filter(a => Number(a.severity) === 6 && matchesSource(a));
      if (apiDownNew.length === 0) return;
      setItems(prev => {
        const seen = new Set(prev.map(p => `${p.cod_empresa}:${p.event_id}`));
        const fresh = apiDownNew.filter(a => !seen.has(`${a.cod_empresa}:${a.event_id}`));
        return sortByClockDesc([...fresh, ...prev]);
      });
    };
    const handleRemoved = (event) => {
      const removed = event.detail;
      if (!removed) return;
      const list = Array.isArray(removed) ? removed : [removed];
      setItems(prev => prev.filter(a => !list.some(r =>
        r.cod_empresa === a.cod_empresa && r.event_id === a.event_id
      )));
    };
    window.addEventListener('alarms-new', handleNew);
    window.addEventListener('alarm-removed', handleRemoved);
    return () => {
      window.removeEventListener('alarms-new', handleNew);
      window.removeEventListener('alarm-removed', handleRemoved);
    };
  }, [matchesSource, sortByClockDesc]);

  if (items.length === 0) return null;

  return (
    <Paper
      elevation={0}
      sx={{
        mb: 2,
        border: `2px solid ${red[700]}`,
        borderRadius: 1,
        overflow: 'hidden',
        bgcolor: 'background.paper',
      }}
    >
      <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, px: 2, py: 1, bgcolor: red[900] }}>
        <ErrorOutlineIcon sx={{ color: '#fff' }} />
        <Typography variant="subtitle1" sx={{ color: '#fff', fontWeight: 700, flex: 1 }}>
          Desconexões de API ativas ({items.length})
        </Typography>
      </Box>
      <Table size="small">
        <TableHead>
          <TableRow sx={{ bgcolor: grey[900] }}>
            <TableCell sx={{ color: grey[100], fontWeight: 600, fontSize: 11, borderBottom: `1px solid ${grey[800]}` }}>Fonte</TableCell>
            <TableCell sx={{ color: grey[100], fontWeight: 600, fontSize: 11, borderBottom: `1px solid ${grey[800]}` }}>Empresa</TableCell>
            <TableCell sx={{ color: grey[100], fontWeight: 600, fontSize: 11, borderBottom: `1px solid ${grey[800]}` }}>Host</TableCell>
            <TableCell sx={{ color: grey[100], fontWeight: 600, fontSize: 11, borderBottom: `1px solid ${grey[800]}` }}>Início</TableCell>
            <TableCell sx={{ color: grey[100], fontWeight: 600, fontSize: 11, borderBottom: `1px solid ${grey[800]}` }}>Tempo offline</TableCell>
            <TableCell sx={{ color: grey[100], fontWeight: 600, fontSize: 11, borderBottom: `1px solid ${grey[800]}` }} align="center">Ação</TableCell>
          </TableRow>
        </TableHead>
        <TableBody>
          {items.map((a) => {
            const src = a.source || 'zabbix';
            const since = a.clock ? moment(a.clock) : null;
            const duration = since ? moment.duration(moment().diff(since)).humanize() : '-';
            const acks = Number(a.acks || 0);
            const cellSx = { fontSize: 12, color: grey[100], borderBottom: `1px solid ${grey[800]}` };
            return (
              <TableRow
                key={`${a.cod_empresa}:${a.event_id}`}
                hover
                sx={{
                  bgcolor: 'rgba(244, 67, 54, 0.12)',
                  '&:hover': { bgcolor: 'rgba(244, 67, 54, 0.22)' },
                }}
              >
                <TableCell sx={cellSx}>
                  <Chip
                    label={src === 'wazuh' ? 'Wazuh' : 'Zabbix'}
                    size="small"
                    sx={{
                      bgcolor: src === 'wazuh' ? orange[700] : blue[700],
                      color: '#fff',
                      fontWeight: 600,
                      fontSize: 10,
                    }}
                  />
                </TableCell>
                <TableCell sx={cellSx}>{a.cod_empresa}</TableCell>
                <TableCell sx={cellSx}>{a.hostname || a.name || '-'}</TableCell>
                <TableCell sx={cellSx}>
                  {since ? since.format('DD/MM HH:mm:ss') : '-'}
                </TableCell>
                <TableCell sx={cellSx}>{duration}</TableCell>
                <TableCell sx={cellSx} align="center">
                  <Tooltip title={acks > 0 ? `${acks} ack(s) — abrir detalhes` : 'Reconhecer alarme'}>
                    <IconButton
                      size="small"
                      onClick={() => onAck && onAck(a)}
                      sx={{ color: acks > 0 ? green[400] : grey[300] }}
                    >
                      <Badge badgeContent={acks} color="success" overlap="circular">
                        {acks > 0 ? <AssignmentTurnedInIcon fontSize="small" /> : <AssignmentLateIcon fontSize="small" />}
                      </Badge>
                    </IconButton>
                  </Tooltip>
                </TableCell>
              </TableRow>
            );
          })}
        </TableBody>
      </Table>
    </Paper>
  );
}
